import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, Paper,
  Chip, CircularProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import LagbasLogo from '../../../../components/LagbasLogo';
import { db } from '../../../../firebase/config';
import {
  doc, getDoc, getDocs, setDoc, deleteDoc, addDoc, updateDoc,
  collection, query, orderBy, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { storage } from '../../../../firebase/config';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Toaster, toast } from 'sonner';
import {
  MAPPN_ACCENT, TECH_TYPES, uid, defaultTechStack,
} from '../constants';

const PRESET_COLORS = [
  '#1565C0','#0a66c2','#2E7D32','#FF6B00','#6A1B9A',
  '#E65100','#00838F','#C62828','#37474F','#F9A825',
];

function ColorPicker({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {PRESET_COLORS.map(c => (
        <Box
          key={c}
          onClick={() => onChange(c)}
          sx={{
            width: 20, height: 20, borderRadius: '50%', bgcolor: c,
            cursor: 'pointer', border: value === c ? '2px solid white' : '2px solid transparent',
            transition: 'border 0.1s',
          }}
        />
      ))}
    </Box>
  );
}

const DF = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' },
  '& .MuiOutlinedInput-root': {
    color: 'white', fontSize: '0.85rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: MAPPN_ACCENT },
  },
};

export default function MappNSettings() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [perspectives, setPerspectives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Case fields
  const [caseName, setCaseName] = useState('');
  const [caseDesc, setCaseDesc] = useState('');
  const [caseTech, setCaseTech] = useState([]);
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [heroVideoUrl, setHeroVideoUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [cSnap, pSnap, catSnap] = await Promise.all([
        getDoc(doc(db, 'mappnCases', caseId)),
        getDocs(query(collection(db, 'mappnCases', caseId, 'perspectives'), orderBy('order'))),
        getDocs(query(collection(db, 'mappnCases', caseId, 'categories'), orderBy('order'))),
      ]);
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      const d = cSnap.data();
      setCaseData({ id: cSnap.id, ...d });
      setCaseName(d.name || '');
      setCaseDesc(d.description || '');
      setCaseTech(d.techStack || defaultTechStack());
      setHeroImageUrl(d.heroImageUrl || '');
      setHeroVideoUrl(d.heroVideoUrl || '');
      setPerspectives(pSnap.docs.map(x => ({ id: x.id, ...x.data(), _editing: false })));
      setCategories(catSnap.docs.map(x => ({ id: x.id, ...x.data(), _editing: false })));
      setLoading(false);
    }
    load();
  }, [caseId, navigate]);

  async function saveCaseMeta() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'mappnCases', caseId), {
        name: caseName.trim(),
        description: caseDesc.trim(),
        techStack: caseTech,
        updatedAt: serverTimestamp(),
      });
      toast.success('Case-inställningar sparade');
    } catch (e) {
      toast.error('Kunde inte spara');
    }
    setSaving(false);
  }

  // ── Hero media ────────────────────────────────────────────────────────────

  async function uploadMedia(type, file) {
    if (!file) return;
    const setUploading = type === 'image' ? setUploadingImage : setUploadingVideo;
    const setUrl = type === 'image' ? setHeroImageUrl : setHeroVideoUrl;
    const field = type === 'image' ? 'heroImageUrl' : 'heroVideoUrl';
    const sRef = storageRef(storage, `mappn/${caseId}/hero-${type}`);
    setUploading(true);
    try {
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      setUrl(url);
      await updateDoc(doc(db, 'mappnCases', caseId), { [field]: url, updatedAt: serverTimestamp() });
      toast.success(type === 'image' ? 'Bild uppladdad' : 'Video uppladdad');
    } catch {
      toast.error('Uppladdning misslyckades');
    }
    setUploading(false);
  }

  async function deleteMedia(type) {
    const setUrl = type === 'image' ? setHeroImageUrl : setHeroVideoUrl;
    const field = type === 'image' ? 'heroImageUrl' : 'heroVideoUrl';
    const sRef = storageRef(storage, `mappn/${caseId}/hero-${type}`);
    try { await deleteObject(sRef); } catch { /* may not exist */ }
    setUrl('');
    await updateDoc(doc(db, 'mappnCases', caseId), { [field]: deleteField(), updatedAt: serverTimestamp() });
    toast.success('Media borttaget');
  }

  // ── Perspectives ─────────────────────────────────────────────────────────

  async function addPerspective() {
    const newP = { name: 'Nytt perspektiv', color: PRESET_COLORS[0], order: perspectives.length, createdAt: serverTimestamp() };
    const ref = await addDoc(collection(db, 'mappnCases', caseId, 'perspectives'), newP);
    setPerspectives(prev => [...prev, { id: ref.id, ...newP, _editing: true }]);
  }

  async function updatePerspective(id, patch) {
    setPerspectives(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
    const { _editing, ...data } = { ...perspectives.find(p => p.id === id), ...patch };
    await setDoc(doc(db, 'mappnCases', caseId, 'perspectives', id), data, { merge: true });
  }

  async function deletePerspective(id) {
    if (!window.confirm('Ta bort perspektivet?')) return;
    await deleteDoc(doc(db, 'mappnCases', caseId, 'perspectives', id));
    setPerspectives(prev => prev.filter(p => p.id !== id));
    toast.success('Perspektiv borttaget');
  }

  // ── Categories ────────────────────────────────────────────────────────────

  async function addCategory() {
    const newC = { name: 'Ny kategori', color: PRESET_COLORS[2], order: categories.length, createdAt: serverTimestamp() };
    const ref = await addDoc(collection(db, 'mappnCases', caseId, 'categories'), newC);
    setCategories(prev => [...prev, { id: ref.id, ...newC, _editing: true }]);
  }

  async function updateCategory(id, patch) {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    const { _editing, ...data } = { ...categories.find(c => c.id === id), ...patch };
    await setDoc(doc(db, 'mappnCases', caseId, 'categories', id), data, { merge: true });
  }

  async function deleteCategory(id) {
    if (!window.confirm('Ta bort kategorin?')) return;
    await deleteDoc(doc(db, 'mappnCases', caseId, 'categories', id));
    setCategories(prev => prev.filter(c => c.id !== id));
    toast.success('Kategori borttagen');
  }

  // ── Tech stack ────────────────────────────────────────────────────────────

  function updateTech(id, patch) {
    setCaseTech(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }

  function deleteTech(id) {
    setCaseTech(prev => prev.filter(t => t.id !== id));
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: MAPPN_ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', color: 'white' }}>
      <Toaster richColors position="top-center" />

      {/* Header */}
      <Box sx={{
        bgcolor: '#161b22', borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {heroImageUrl && (
          <Box component="img" src={heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{caseData?.name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
          ].map(tab => (
            <Button
              key={tab.path}
              startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color: tab.path === 'settings' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'settings' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1,
                '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 860, mx: 'auto', px: 3, py: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* ── Case metadata ─────────────────────────────────────────────── */}
        <SettingsCard title="Case">
          <TextField label="Namn" value={caseName} onChange={e => setCaseName(e.target.value)}
            fullWidth size="small" sx={{ ...DF, mb: 1.5 }} />
          <TextField label="Beskrivning" value={caseDesc} onChange={e => setCaseDesc(e.target.value)}
            fullWidth size="small" multiline rows={2} sx={DF} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" disabled={saving || !caseName.trim()} onClick={saveCaseMeta}
              sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none' }}>
              {saving ? 'Sparar…' : 'Spara ändringar'}
            </Button>
          </Box>
        </SettingsCard>

        {/* ── Hero media ────────────────────────────────────────────────── */}
        <SettingsCard title="Omslagsbild / Hero-media">
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mb: 2, lineHeight: 1.6 }}>
            Bild och/eller video som visas som avatar och bakgrund i vyerna.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>

            {/* Image */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                Bild
              </Typography>
              {heroImageUrl ? (
                <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', display: 'inline-block', width: '100%', maxWidth: 280 }}>
                  <Box component="img" src={heroImageUrl} sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                  <IconButton size="small" onClick={() => deleteMedia('image')} sx={{
                    position: 'absolute', top: 6, right: 6,
                    bgcolor: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: '#EF5350', bgcolor: 'rgba(0,0,0,0.85)' },
                  }}>
                    <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Box>
              ) : (
                <Box component="label" sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  width: '100%', maxWidth: 280, height: 130, borderRadius: 1.5,
                  border: '1.5px dashed rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)',
                  cursor: uploadingImage ? 'default' : 'pointer', gap: 0.75,
                  '&:hover': { borderColor: MAPPN_ACCENT + '55', bgcolor: MAPPN_ACCENT + '08' },
                  transition: 'all 0.15s',
                }}>
                  {uploadingImage
                    ? <CircularProgress size={22} sx={{ color: MAPPN_ACCENT }} />
                    : <>
                        <ImageIcon sx={{ color: 'rgba(255,255,255,0.15)', fontSize: '1.8rem' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)' }}>Klicka för att ladda upp</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.12)' }}>JPG · PNG · WebP · GIF</Typography>
                      </>
                  }
                  <input type="file" accept="image/*" hidden onChange={e => uploadMedia('image', e.target.files[0])} />
                </Box>
              )}
            </Box>

            {/* Video */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                Video (loop)
              </Typography>
              {heroVideoUrl ? (
                <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', display: 'inline-block', width: '100%', maxWidth: 280 }}>
                  <Box component="video" src={heroVideoUrl} autoPlay loop muted playsInline
                    sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                  <IconButton size="small" onClick={() => deleteMedia('video')} sx={{
                    position: 'absolute', top: 6, right: 6,
                    bgcolor: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: '#EF5350', bgcolor: 'rgba(0,0,0,0.85)' },
                  }}>
                    <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Box>
              ) : (
                <Box component="label" sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  width: '100%', maxWidth: 280, height: 130, borderRadius: 1.5,
                  border: '1.5px dashed rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)',
                  cursor: uploadingVideo ? 'default' : 'pointer', gap: 0.75,
                  '&:hover': { borderColor: MAPPN_ACCENT + '55', bgcolor: MAPPN_ACCENT + '08' },
                  transition: 'all 0.15s',
                }}>
                  {uploadingVideo
                    ? <CircularProgress size={22} sx={{ color: MAPPN_ACCENT }} />
                    : <>
                        <VideoLibraryIcon sx={{ color: 'rgba(255,255,255,0.15)', fontSize: '1.8rem' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)' }}>Klicka för att ladda upp</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.12)' }}>MP4 · WebM</Typography>
                      </>
                  }
                  <input type="file" accept="video/*" hidden onChange={e => uploadMedia('video', e.target.files[0])} />
                </Box>
              )}
            </Box>
          </Box>
        </SettingsCard>

        {/* ── Perspectives ──────────────────────────────────────────────── */}
        <SettingsCard title="Perspektiv">
          {perspectives.map(p => (
            <Box key={p.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: p.color, flexShrink: 0, mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  value={p.name}
                  onChange={e => updatePerspective(p.id, { name: e.target.value })}
                  size="small" fullWidth sx={{ ...DF, mb: 1 }}
                  inputProps={{ style: { fontWeight: 700 } }}
                />
                <ColorPicker value={p.color} onChange={color => updatePerspective(p.id, { color })} />
              </Box>
              <IconButton size="small" onClick={() => deletePerspective(p.id)}
                sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addPerspective}
            sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.82rem' }}>
            Lägg till perspektiv
          </Button>
        </SettingsCard>

        {/* ── Categories ────────────────────────────────────────────────── */}
        <SettingsCard title="Kategorier">
          {categories.map(c => (
            <Box key={c.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c.color, flexShrink: 0, mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  value={c.name}
                  onChange={e => updateCategory(c.id, { name: e.target.value })}
                  size="small" fullWidth sx={{ ...DF, mb: 1 }}
                  inputProps={{ style: { fontWeight: 700 } }}
                />
                <ColorPicker value={c.color} onChange={color => updateCategory(c.id, { color })} />
              </Box>
              <IconButton size="small" onClick={() => deleteCategory(c.id)}
                sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addCategory}
            sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.82rem' }}>
            Lägg till kategori
          </Button>
        </SettingsCard>

        {/* ── Case tech stack ───────────────────────────────────────────── */}
        <SettingsCard title="Tech Stack (casenivå)">
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mb: 1.5 }}>
            Dessa poster ärvs av alla komponenter i caset. Komponentspecifika tillägg görs i varje komponent.
          </Typography>

          {caseTech.map(tech => {
            const tt = TECH_TYPES.find(x => x.key === tech.type) || TECH_TYPES[7];
            return (
              <Box key={tech.id} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'center' }}>
                <Chip label={tt.label} size="small"
                  sx={{ bgcolor: tt.bg, color: tt.color, fontSize: '0.65rem', height: 20, width: 80, flexShrink: 0 }} />
                <select
                  value={tech.type}
                  onChange={e => updateTech(tech.id, { type: e.target.value })}
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'white', borderRadius: 4, padding: '4px 8px', fontSize: '0.78rem',
                    width: 110, flexShrink: 0,
                  }}
                >
                  {TECH_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <TextField
                  placeholder="Namn *"
                  value={tech.name}
                  onChange={e => updateTech(tech.id, { name: e.target.value })}
                  size="small" sx={{ ...DF, flex: 2 }}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                />
                <TextField
                  placeholder="Version"
                  value={tech.version}
                  onChange={e => updateTech(tech.id, { version: e.target.value })}
                  size="small" sx={{ ...DF, width: 90 }}
                  inputProps={{ style: { fontSize: '0.78rem' } }}
                />
                <TextField
                  placeholder="URL"
                  value={tech.url}
                  onChange={e => updateTech(tech.id, { url: e.target.value })}
                  size="small" sx={{ ...DF, flex: 2 }}
                  inputProps={{ style: { fontSize: '0.78rem' } }}
                />
                <IconButton size="small" onClick={() => deleteTech(tech.id)}
                  sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#EF5350' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setCaseTech(prev => [...prev, { id: uid(), name: '', type: 'package', version: '', url: '', note: '' }])}
              sx={{ color: MAPPN_ACCENT, textTransform: 'none', fontSize: '0.82rem' }}>
              Lägg till tech
            </Button>
            <Button size="small" onClick={saveCaseMeta} disabled={saving}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.82rem', ml: 'auto' }}>
              {saving ? 'Sparar…' : 'Spara tech stack'}
            </Button>
          </Box>
        </SettingsCard>

      </Box>
    </Box>
  );
}

function SettingsCard({ title, children }) {
  return (
    <Paper sx={{
      bgcolor: '#161b22',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 2, p: 2.5,
    }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white', mb: 2, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
